import { Session } from '@ory/kratos-client';
import i18n from 'i18next';
import { LoaderFunctionArgs } from 'react-router-dom';
import { mainStore } from 'common/clients/jotai-store.client.ts';
import { queryKeysClient } from 'common/clients/query-key.client.ts';
import { queryClient } from 'common/clients/query.client.ts';
import { ENV_API_POSTFIX } from 'common/constants/env.constants.ts';
import { EQueryKeys } from 'common/enums/query-keys.enums.ts';
import { EUrlQueryParams } from 'common/enums/url-query-params.enums.ts';
import { profileConfigAtom } from 'common/stores/profile-config.store.ts';
import {
  getTransactionGuidFromCookie,
  setTransactionGuidToCookie,
} from 'common/utils/cookies.utils.ts';
import { captureError, IError } from 'common/utils/error.utils.ts';
import { isValidUrl, setHttps } from 'common/utils/url.utils.ts';
import {
  ITransactionInfoDataRes,
} from 'domains/transaction/interfaces/transaction.interfaces.ts';
import { IUserProfileConfigRes } from 'domains/user/interfaces/user.interfaces.ts';
import { postUserProfileConfigService } from 'domains/user/services/user.service.ts';
import { IWalletDataRes } from 'domains/wallet/inrefaces/wallet.interfaces.ts';
import { APP_CONFIG, ELanguages } from '../../app.config.ts';

export interface INeutralRouteGuardRootLoader {
  getGeneralTransactionInfoData: ITransactionInfoDataRes | undefined;
  getWalletData: IWalletDataRes | undefined;
  getToSessionData: Session | undefined;
  userIsAuthorized: boolean;
  getUserProfileConfigData: IUserProfileConfigRes | undefined;
}

export const neutralRouteGuardRootLoader = async (
  { request }: LoaderFunctionArgs
): Promise<INeutralRouteGuardRootLoader> => {
  let getToSessionData: Session | undefined;
  const reqOptions = {
    from: request.url,
  };

  try {
    const getToSessionQuery = queryKeysClient[EQueryKeys.AuthQueryKeys].getToSession();
    getToSessionData =
      await queryClient.getQueryData(getToSessionQuery.queryKey)
      ?? await queryClient.fetchQuery(getToSessionQuery);
  } catch (error) {
    console.error(error);
  }

  try {
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const interactiveIdFromUrlQueryParamValue = searchParams.get(EUrlQueryParams.InteractiveId) ?? '';

    const returnToQueryParamValue = searchParams.get(EUrlQueryParams.ReturnTo) ?? '';
    let returnToDomainName = '';
    let interactiveIdFromRedirectUrlQueryParamValue = '';

    if (returnToQueryParamValue && isValidUrl(returnToQueryParamValue)) {
      const returnUrl = new URL(returnToQueryParamValue);
      const { searchParams: searchParamsReturnUrl } = returnUrl;
      returnToDomainName = returnUrl.origin;
      interactiveIdFromRedirectUrlQueryParamValue =
        searchParamsReturnUrl.get(EUrlQueryParams.InteractiveId) ?? '';
    }

    const transactionIDFromCookiesStore = getTransactionGuidFromCookie();
    const interactiveIdQueryParamValue = interactiveIdFromUrlQueryParamValue
      || interactiveIdFromRedirectUrlQueryParamValue
      || transactionIDFromCookiesStore;

    if (interactiveIdQueryParamValue) {
      setTransactionGuidToCookie(interactiveIdQueryParamValue);
    }

    let getGeneralTransactionInfoData: ITransactionInfoDataRes | undefined;

    if (interactiveIdQueryParamValue) {
      const getGeneralTransactionInfoDataQuery =
        queryKeysClient[EQueryKeys.TransactionKeys].getGeneralTransactionInfoData({
          sep24GUID: interactiveIdQueryParamValue,
          reqOptions,
        });
      getGeneralTransactionInfoData = await queryClient.getQueryData(getGeneralTransactionInfoDataQuery.queryKey)
        ?? await queryClient.fetchQuery(getGeneralTransactionInfoDataQuery);
    }

    const getWalletDataQuery = queryKeysClient[EQueryKeys.WalletKeys].getWalletData({
      reqConfig: {
        ...reqOptions,
        ...(returnToDomainName ? { baseURL: setHttps(`${returnToDomainName}${ENV_API_POSTFIX}`) } : {}),
      },
    });
    const getWalletData: IWalletDataRes =
      await queryClient.getQueryData(getWalletDataQuery.queryKey)
      ?? await queryClient.fetchQuery(getWalletDataQuery);

    let userIsAuthorized = false;
    let getUserProfileConfigData: IUserProfileConfigRes | undefined;

    if (!(i18n.language in APP_CONFIG.LOCALES)) {
      await i18n.changeLanguage(APP_CONFIG.LOCALE_DEFAULT);
    }

    // Check if user is authorized.
    if (getToSessionData?.active) {
      userIsAuthorized = true;

      const getUserProfileConfigQuery = queryKeysClient[EQueryKeys.UserKeys].getUserProfileConfig({ reqOptions });
      getUserProfileConfigData = await queryClient.fetchQuery(getUserProfileConfigQuery);

      const getUserProfileDataQuery = queryKeysClient[EQueryKeys.UserKeys].getUserProfileData();
      await queryClient.fetchQuery(getUserProfileDataQuery);

      // Set lang from config
      if (getUserProfileConfigData?.lang && getUserProfileConfigData.lang in APP_CONFIG.LOCALES) {
        await i18n.changeLanguage(getUserProfileConfigData.lang);
      }

      if (getUserProfileConfigData?.has_pending_action) {
        const transactionsReqParams = {
          limit: 1,
        };
        const transactionsDataQuery = queryKeysClient[EQueryKeys.TransactionKeys]
          .getFirstIncompleteTransactionFromTransactionsData({
            reqParams: transactionsReqParams
          });
        await queryClient.fetchQuery(transactionsDataQuery);
      }

      // Set profile data to store
      mainStore.set(profileConfigAtom, (data) => ({
        ...data,
        userIsAuthorized: true,
        profile: getUserProfileConfigData,
      }));
    }

    // URL language param takes priority over cookie and profile config for all users
    const languageQueryParamValue = searchParams.get(EUrlQueryParams.Language) ?? '';
    const isValidLanguageQueryParam = languageQueryParamValue in APP_CONFIG.LOCALES;

    if (isValidLanguageQueryParam) {
      await i18n.changeLanguage(languageQueryParamValue as ELanguages);

      if (userIsAuthorized && getUserProfileConfigData?.lang !== (languageQueryParamValue as ELanguages)) {
        try {
          await postUserProfileConfigService({ lang: languageQueryParamValue as ELanguages });
        } catch {
          // don't block navigation if persist fails
        }
      }
    }

    return {
      getGeneralTransactionInfoData,
      getWalletData,
      getToSessionData,
      userIsAuthorized,
      getUserProfileConfigData,
    };
  } catch (error: unknown) {
    captureError(error as IError);

    return {
      getGeneralTransactionInfoData: undefined,
      getWalletData: undefined,
      getToSessionData: undefined,
      userIsAuthorized: false,
      getUserProfileConfigData: undefined,
    };
  }
};
